import { Home, LayoutGrid, Moon, Settings } from 'lucide-react';
import { verbMatcher, type AssistantCommand, type WebModule } from '@nw/web-platform';

const clean = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, '')
    .trim();

/** Commands owned by the shell itself: moving between spaces and the atmosphere. */
export function coreCommands(opts: { modules: WebModule[]; openSpaces: () => void; setAtmosphere: (a: 'auto' | 'aurora' | 'fog' | 'deep') => Promise<void> }): AssistantCommand[] {
  const targets = [
    ...opts.modules
      .filter((m) => m.nav)
      .map((m) => ({
        names: [m.nav!.label, m.id, m.nav!.label.split(' ')[0]!],
        path: m.nav!.path,
        label: m.nav!.label,
      })),
    {
      names: ['settings', 'preferences'],
      path: '/settings',
      label: 'Settings',
    },
  ];
  const findTarget = (arg?: string) => {
    if (!arg) return null;
    const a = clean(arg);
    return targets.find((t) => t.names.some((n) => clean(n) === a || clean(n).startsWith(a))) ?? null;
  };
  const openMatch = verbMatcher(['open', 'go to', 'show', 'launch', 'switch to'], { argRequired: true });
  return [
    {
      id: 'core.open',
      title: 'Go to a space',
      usage: 'open <library | mind | settings>',
      icon: LayoutGrid,
      needsArg: true,
      match: (text) => {
        const m = openMatch(text);
        if (!m) return null;
        if (m.arg && !findTarget(m.arg)) return { score: 0.3, arg: m.arg };
        return m;
      },
      run: async (arg, ctx) => {
        const t = findTarget(arg);
        if (!t)
          return {
            ok: false,
            message: `I don't know a module called “${arg}”.`,
          };
        ctx.navigate(t.path);
        return { ok: true, message: `Opened ${t.label}` };
      },
    },
    {
      id: 'core.home',
      title: 'Go to Homebase',
      usage: 'home',
      icon: Home,
      match: verbMatcher(['home', 'homebase', 'go home', 'desktop']),
      run: async (_, ctx) => {
        ctx.navigate('/');
        return { ok: true, message: 'Homebase' };
      },
    },
    {
      id: 'core.spaces',
      title: 'Show all spaces',
      usage: 'spaces',
      icon: LayoutGrid,
      match: verbMatcher(['spaces', 'all spaces', 'apps', 'modules', 'overview', 'windows']),
      run: async () => {
        opts.openSpaces();
        return { ok: true, message: 'All spaces' };
      },
    },
    {
      id: 'core.atmosphere',
      title: 'Change the atmosphere',
      usage: 'atmosphere <aurora | fog | deep | auto>',
      icon: Moon,
      needsArg: true,
      match: verbMatcher(['atmosphere', 'theme', 'switch atmosphere to', 'set atmosphere'], { argRequired: true }),
      run: async (arg) => {
        const a = clean(arg ?? '') as 'auto' | 'aurora' | 'fog' | 'deep';
        if (!['auto', 'aurora', 'fog', 'deep'].includes(a)) return { ok: false, message: 'Choose aurora, fog, deep or auto.' };
        await opts.setAtmosphere(a);
        return { ok: true, message: `Atmosphere: ${a}` };
      },
    },
    {
      id: 'core.settings',
      title: 'Settings',
      usage: 'settings',
      icon: Settings,
      match: verbMatcher(['settings', 'preferences']),
      run: async (_, ctx) => {
        ctx.navigate('/settings');
        return { ok: true, message: 'Settings' };
      },
    },
  ];
}
